/**
 * Site Metadata and SEO Configuration
 * Source: coming-soon-technical-design-for-backend-dev.md
 * Author: 07 Backend Developer
 * Date: 2026-03-06
 *
 * Centralized config for:
 * - Site title, description, canonical URL
 * - Open Graph (OG) metadata for social sharing
 * - Twitter Card metadata
 * - Navigation and contact links
 * - SEO and CMS settings
 */

export const siteConfig = {
  // ============================================================================
  // Core Brand Metadata
  // ============================================================================

  name: 'PromptVan',
  shortName: 'PromptVan',
  description: 'Be the first to know. Join the waitlist and get an exclusive free resource.',
  longDescription:
    'PromptVan is coming soon. Join the waitlist for early access and an exclusive free guide with 50+ expert resources.',

  // ============================================================================
  // URLs and Domains
  // ============================================================================

  url: 'https://promptvan.com',
  baseUrl: process.env.NEXT_PUBLIC_APP_URL || 'https://promptvan.com',
  canonical: 'https://promptvan.com',

  // ============================================================================
  // Contact and Social
  // ============================================================================

  email: 'contact@promptvan.com',
  twitterHandle: '@promptvan', // Twitter handle (if applicable)
  // social: {
  //   twitter: 'https://twitter.com/promptvan',
  //   linkedin: 'https://linkedin.com/company/promptvan',
  //   github: 'https://github.com/promptvan',
  // },

  // ============================================================================
  // Images (OG, Twitter Cards, etc.)
  // ============================================================================

  images: {
    og: '/og-image.png', // 1200x630 recommended for Open Graph
    twitter: '/og-image.png', // 1200x675 recommended for Twitter
    logo: '/logo.svg',
  },

  // ============================================================================
  // Open Graph (Social Sharing) Configuration
  // ============================================================================

  openGraph: {
    title: 'PromptVan - Something is coming',
    description: 'Be the first to know. Join the waitlist and get an exclusive free resource.',
    url: 'https://promptvan.com',
    siteName: 'PromptVan',
    locale: 'en_US',
    type: 'website',
    images: [
      {
        url: 'https://promptvan.com/og-image.png',
        width: 1200,
        height: 630,
        alt: 'PromptVan - Something is coming',
      },
    ],
  },

  // ============================================================================
  // Twitter Card Configuration
  // ============================================================================

  twitter: {
    card: 'summary_large_image',
    site: '@promptvan',
    creator: '@promptvan',
    title: 'PromptVan - Something is coming',
    description: 'Be the first to know. Join the waitlist and get an exclusive free resource.',
    image: 'https://promptvan.com/og-image.png',
  },

  // ============================================================================
  // SEO and Crawling
  // ============================================================================

  robots: {
    index: true,
    follow: true,
    googleBot: {
      index: true,
      follow: true,
      'max-video-preview': -1,
      'max-image-preview': 'large',
      'max-snippet': -1,
    },
  },

  // ============================================================================
  // Structured Data (JSON-LD)
  // ============================================================================

  structuredData: {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'PromptVan',
    url: 'https://promptvan.com',
    logo: 'https://promptvan.com/logo.svg',
    description: 'Be the first to know. Join the waitlist and get an exclusive free resource.',
    email: 'contact@promptvan.com',
    // sameAs: [
    //   'https://twitter.com/promptvan',
    //   'https://linkedin.com/company/promptvan',
    // ],
  },

  // ============================================================================
  // Navigation Links (if needed)
  // ============================================================================

  // links: {
  //   privacy: '/privacy',
  //   terms: '/terms',
  //   contact: 'mailto:hello@promptvan.com',
  // },

  // ============================================================================
  // CMS and Content Settings
  // ============================================================================

  i18n: {
    defaultLocale: 'en',
    locales: ['en'],
  },

  // ============================================================================
  // Security and Headers
  // ============================================================================

  // These headers should be set in next.config.js or vercel.json
  // and are referenced here for documentation:
  securityHeaders: {
    'Strict-Transport-Security': 'max-age=31536000; includeSubDomains',
    'X-Frame-Options': 'DENY',
    'X-Content-Type-Options': 'nosniff',
    'X-XSS-Protection': '1; mode=block',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'Content-Security-Policy': "default-src 'self'; script-src 'self' 'unsafe-inline'; style-src 'self' 'unsafe-inline'",
  },
}

/**
 * Metadata type for TypeScript
 */
export type SiteConfig = typeof siteConfig
